import { p, h, ul, code, tip, info, warn, table, cards } from './helpers';

const course = {
  id: 'auth-and-security',
  step: 6,
  title: 'Database, Auth and Security',
  tagline: 'Subscribers in MySQL, digest authentication, flood bans, access lists, TLS and accounting.',
  icon: 'ShieldCheck',
  colors: ['#ff4d6d', '#c026d3'],
  level: 'Intermediate',
  lessons: [
    {
      id: 'database',
      title: 'Setting up the database',
      icon: 'Database',
      minutes: 10,
      summary: 'Create the Kamailio schema in MySQL or MariaDB and connect the config to it.',
      blocks: [
        p('Kamailio keeps subscribers, registrations, ACLs, dispatcher lists and call records in a SQL database. MySQL/MariaDB and PostgreSQL are the most common choices; SQLite works for labs.'),
        code('bash', `
sudo apt install mariadb-server kamailio-mysql-modules

# /etc/kamailio/kamctlrc
SIP_DOMAIN=example.com
DBENGINE=MYSQL
DBHOST=localhost
DBNAME=kamailio
DBRWUSER="kamailio"
DBRWPW="change-me-rw"
DBROUSER="kamailioro"
DBROPW="change-me-ro"`, 'Install and edit kamctlrc'),
        code('bash', `
sudo kamdbctl create     # asks for the DB root password, creates users and tables`),
        p('Answer the prompts for the optional presence and extra tables as needed. Then switch the config over to the database:'),
        code('cfg', `
#!define WITH_MYSQL
#!define WITH_AUTH
#!define WITH_USRLOCDB

#!ifdef WITH_MYSQL
#!define DBURL "mysql://kamailio:change-me-rw@localhost/kamailio"
#!endif

loadmodule "db_mysql.so"`),
        h('Key tables'),
        table(['Table', 'Used by', 'Holds'], [
          ['`subscriber`', 'auth_db', 'Username, domain, password, ha1, ha1b'],
          ['`location`', 'usrloc', 'Registered contacts when db_mode > 0'],
          ['`address`', 'permissions', 'Trusted IP addresses and subnets'],
          ['`dispatcher`', 'dispatcher', 'Load-balancer destinations (alternative to a list file)'],
          ['`acc` / `missed_calls`', 'acc', 'Accounting records'],
          ['`version`', 'all', 'Schema version per table. Kamailio refuses to start if it does not match.'],
        ]),
        warn('Change the default passwords', 'Older kamctlrc files ship with the well-known passwords `kamailiorw` and `kamailioro`. Set your own before running `kamdbctl create`, and use the same value in `DBURL`.'),
      ],
    },
    {
      id: 'digest-auth',
      title: 'Digest authentication',
      icon: 'LockKeyhole',
      minutes: 11,
      summary: 'Challenge REGISTER and INVITE, verify against the subscriber table, and refuse to be an open relay.',
      blocks: [
        code('cfg', `
loadmodule "auth.so"
loadmodule "auth_db.so"

modparam("auth_db", "db_url", DBURL)
modparam("auth_db", "calculate_ha1", yes)       # passwords stored in clear text
modparam("auth_db", "password_column", "password")
modparam("auth_db", "load_credentials", "")
modparam("auth_db", "use_domain", MULTIDOMAIN)`, 'Modules'),
        code('cfg', `
route[AUTH] {
#!ifdef WITH_AUTH
    if (is_method("REGISTER") || from_uri == myself) {
        # authenticate requests from our own users
        if (!auth_check("$fd", "subscriber", "1")) {
            auth_challenge("$fd", "0");
            exit;
        }
        # user authenticated - remove auth header
        if (!is_method("REGISTER|PUBLISH")) {
            consume_credentials();
        }
    }
    # caller is not local and callee is not local: not an open relay
    if (from_uri != myself && uri != myself) {
        sl_send_reply("403", "Not relaying");
        exit;
    }
#!endif
    return;
}`, 'route[AUTH] from the default config'),
        ul([
          '`auth_check(realm, table, flags)` picks `www_authenticate` for REGISTER and `proxy_authenticate` for everything else. Flag `1` also checks that the From user matches the authenticated user, so Alice cannot place calls as Bob.',
          '`auth_challenge(realm, flags)` sends 401 for REGISTER and 407 for other requests, with a fresh nonce.',
          '`consume_credentials()` removes the Authorization header before the request is forwarded, so your password hash is not sent to the callee.',
        ]),
        h('Managing subscribers'),
        code('bash', `
kamctl add alice@example.com 'Alic3-Str0ng!'
kamctl add bob@example.com   'B0b-Str0ng!'
kamctl passwd alice@example.com 'N3w-Passw0rd'
kamctl rm bob@example.com`),
        tip('Store HA1, not passwords', 'Set `calculate_ha1` to `0` and `password_column` to `ha1`, and fill the `ha1` column with MD5(username:realm:password). A leaked database then does not reveal clear-text passwords (though HA1 still works for this realm, so protect it).'),
        warn('The open relay check matters', 'Without the `from_uri != myself && uri != myself` check, anyone on the internet can use your server to send calls to anywhere. Toll fraud through open relays costs real money every day.'),
      ],
    },
    {
      id: 'pike-htable',
      title: 'Flood protection with pike and htable',
      icon: 'Flame',
      minutes: 10,
      summary: 'Detect sources sending too many requests and ban them for a while in shared memory.',
      blocks: [
        code('cfg', `
loadmodule "pike.so"
loadmodule "htable.so"

# pike: more than 16 requests per 2 seconds from one IP is a flood
modparam("pike", "sampling_time_unit", 2)
modparam("pike", "reqs_density_per_unit", 16)
modparam("pike", "remove_latency", 4)

# htable: ban list, entries expire after 300 seconds
modparam("htable", "htable", "ipban=>size=8;autoexpire=300;")`),
        code('cfg', `
# in route[REQINIT]
if (src_ip != myself) {
    if ($sht(ipban=>$si) != $null) {
        # ip is already blocked
        xdbg("request from blocked IP - $rm from $fu (IP:$si:$sp)\\n");
        exit;
    }
    if (!pike_check_req()) {
        xlog("L_ALERT", "ALERT: pike blocking $rm from $fu (IP:$si:$sp)\\n");
        $sht(ipban=>$si) = 1;
        exit;
    }
}`),
        h('Managing bans at runtime'),
        code('bash', `
kamcmd htable.dump ipban                  # list banned IPs
kamcmd htable.delete ipban 198.51.100.7   # unban one IP
kamcmd htable.flush ipban                 # unban everyone`),
        h('Blocking brute-force password guessing'),
        p('htable can also count failures. This snippet bans an IP for 15 minutes after 10 failed authentications:'),
        code('cfg', `
modparam("htable", "htable", "authfail=>size=8;autoexpire=900;")

# inside route[AUTH], before auth_check:
if ($sht(authfail=>$si) != $null && $sht(authfail=>$si) >= 10) {
    exit;
}
if (!auth_check("$fd", "subscriber", "1")) {
    if ($au != $null) {
        # credentials were sent but were wrong
        $var(fails) = $shtinc(authfail=>$si);   # atomic increment
    }
    auth_challenge("$fd", "0");
    exit;
}`),
        info('Trusted peers', 'Your PBXs and carriers may legitimately send bursts. Skip pike for addresses you trust (see the permissions module in the next lesson), or tune the density for them.'),
      ],
    },
    {
      id: 'permissions',
      title: 'Access control with permissions',
      icon: 'ListFilter',
      minutes: 9,
      summary: 'Trust your PBXs and carriers by IP address instead of username and password.',
      blocks: [
        p('Gateways and PBX servers usually do not register; they are trusted by IP address. The **permissions** module keeps an address table in memory, loaded from the database.'),
        code('cfg', `
loadmodule "permissions.so"
modparam("permissions", "db_url", DBURL)
modparam("permissions", "db_mode", 1)       # cache the table in memory`),
        code('bash', `
# kamctl address add <group> <ip> <mask> <port> <tag>
kamctl address add 1 10.0.0.20 32 0 "asterisk-1"
kamctl address add 2 203.0.113.0 24 0 "carrier-a"
kamcmd permissions.addressReload
kamcmd permissions.addressDump`),
        code('cfg', `
route[AUTH] {
    # group 1: our PBXs are trusted without digest auth
    if (allow_source_address("1")) {
        return;
    }
    # group 2: carrier sends us inbound DIDs only
    if (allow_source_address("2")) {
        if (uri != myself) {
            sl_send_reply("403", "Forbidden");
            exit;
        }
        return;
    }
    # everyone else must authenticate
    ...
}`),
        cards([
          { icon: 'Fingerprint', title: 'allow_source_address(group)', text: 'True if the source IP and port are in that address group.' },
          { icon: 'Search', title: 'allow_address(group, ip, port)', text: 'Check any IP, for example one from a header.' },
          { icon: 'Hash', title: 'allow_source_address_group()', text: 'Returns the group the source belongs to, or -1.' },
        ]),
        tip('Defense in depth', 'Combine IP trust with a firewall that allows SIP from carriers only on the ports they use, and keep management ports (RPC, database) unreachable from the internet.'),
      ],
    },
    {
      id: 'tls',
      title: 'TLS: encrypted signaling',
      icon: 'Lock',
      minutes: 9,
      summary: 'Listen on port 5061 with certificates, so passwords, numbers and SDP keys are encrypted.',
      blocks: [
        code('cfg', `
enable_tls=yes
listen=tls:203.0.113.10:5061

loadmodule "tls.so"
modparam("tls", "config", "/etc/kamailio/tls.cfg")`, 'kamailio.cfg'),
        code('ini', `
[server:default]
method = TLSv1.2+
verify_certificate = no
require_certificate = no
private_key = /etc/letsencrypt/live/sip.example.com/privkey.pem
certificate = /etc/letsencrypt/live/sip.example.com/fullchain.pem

[client:default]
method = TLSv1.2+
verify_certificate = yes
require_certificate = yes
ca_list = /etc/ssl/certs/ca-certificates.crt`, '/etc/kamailio/tls.cfg'),
        ul([
          'A `[server:...]` profile applies to connections clients open to Kamailio; a `[client:...]` profile to connections Kamailio opens, e.g. to a carrier.',
          'You can add profiles per IP and port, such as `[server:203.0.113.10:5061]`, and SNI-based profiles with `server_name`.',
          '`kamcmd tls.reload` reloads certificates without a restart, useful after a Let\'s Encrypt renewal.',
          '`kamcmd tls.list` shows active TLS connections.',
        ]),
        info('Do not forget the media', 'TLS protects signaling only. To encrypt audio, the endpoints must also use SRTP (SDES keys inside the TLS-protected SDP, or DTLS-SRTP).'),
      ],
    },
    {
      id: 'accounting',
      title: 'Accounting and CDRs',
      icon: 'ReceiptText',
      minutes: 8,
      summary: 'Record call starts, ends and failures with the acc module.',
      blocks: [
        code('cfg', `
#!define FLT_ACC 1
#!define FLT_ACCMISSED 2
#!define FLT_ACCFAILED 3

loadmodule "acc.so"

modparam("acc", "early_media", 0)
modparam("acc", "report_cancels", 0)
modparam("acc", "detect_direction", 0)
modparam("acc", "log_flag", FLT_ACC)
modparam("acc", "log_missed_flag", FLT_ACCMISSED)
modparam("acc", "failed_transaction_flag", FLT_ACCFAILED)
modparam("acc", "log_extra", "src_user=$fU;src_domain=$fd;src_ip=$si;dst_user=$tU;dst_domain=$td")

#!ifdef WITH_ACCDB
modparam("acc", "db_flag", FLT_ACC)
modparam("acc", "db_missed_flag", FLT_ACCMISSED)
modparam("acc", "db_url", DBURL)
modparam("acc", "db_extra", "src_user=$fU;src_domain=$fd;src_ip=$si;dst_user=$tU;dst_domain=$td")
#!endif`),
        p('Set `FLT_ACC` on INVITE and on BYE (the default config already does this). The acc module writes one record when the INVITE succeeds and one when the BYE succeeds. Matching them by Call-ID gives you start time, end time and duration.'),
        table(['Flag set on', 'Record written'], [
          ['INVITE with FLT_ACC', 'Call start, when the 200 OK is forwarded'],
          ['BYE with FLT_ACC', 'Call end'],
          ['INVITE with FLT_ACCMISSED', 'Missed call, to the missed_calls table, when the INVITE fails'],
          ['FLT_ACCFAILED', 'Failed transactions, e.g. 486 or 404'],
        ]),
        tip('Single-record CDRs', 'With the dialog module loaded and `cdr_enable` set in acc, Kamailio can write one complete CDR per call (start, end, duration) instead of separate start and stop records.'),
      ],
    },
  ],
  quiz: [
    { q: 'Which command creates the Kamailio database tables?', options: ['kamctl add', 'kamdbctl create', 'kamcmd db.create', 'kamailio -c'], answer: 1, explain: 'kamdbctl create reads kamctlrc and creates the users and the schema.' },
    { q: 'What does flag 1 in auth_check("$fd", "subscriber", "1") add?', options: ['Disables the nonce', 'Checks that the From user matches the authenticated user', 'Uses TLS', 'Stores the password'], answer: 1, explain: 'It prevents a user from authenticating with their own credentials while pretending to be someone else in the From header.' },
    { q: 'Which pair of modules bans flooding IPs in the default config?', options: ['tm and sl', 'pike and htable', 'rr and maxfwd', 'acc and dialog'], answer: 1, explain: 'pike detects floods and htable stores banned IPs with auto-expiry.' },
    { q: 'Which function trusts a request because of the IP address it came from?', options: ['auth_check()', 'allow_source_address()', 'has_totag()', 'lookup()'], answer: 1, explain: 'allow_source_address() checks the source IP against an address group from the permissions module.' },
    { q: 'What does TLS on port 5061 encrypt?', options: ['Only the audio', 'The SIP signaling', 'Both signaling and audio automatically', 'Only the database'], answer: 1, explain: 'TLS protects SIP signaling. Media needs SRTP to be encrypted.' },
  ],
};

export default course;
